/* eslint-disable max-lines */
import React, { useState, useMemo, useEffect } from 'react'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { HStack, chakra, VStack, Text, Box } from '@chakra-ui/react'
import { Step, Steps, useSteps } from 'chakra-ui-steps'
import dayjs from 'dayjs'
import { EnvironmentEnum, LocalStorageKeyEnum, ESessionStorageKey } from 'enums/common'
import { EBreakPoint, EZIndexLayer } from 'enums/theme'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import useUnsavedWarning from 'hooks/useUnsavedWarning'
import delay from 'lodash/delay'
import get from 'lodash/get'
import identity from 'lodash/identity'
import omit from 'lodash/omit'
import pickBy from 'lodash/pickBy'
import { observer } from 'mobx-react'
import { NextRouter, useRouter } from 'next/router'
import { FormProvider, useForm, useWatch, UseFormReturn } from 'react-hook-form'
import { toast } from 'react-toastify'
import { CommonError } from 'types'
import { handleError } from 'API/error'
import { upsertHousesApplicationByApplicationId } from 'API/houseApplication'
import { IStripeClient } from 'interfaces/stripe'
import routes from 'routes'
import { extractFromFullName, getRawUSPhoneNumber, getValidArray, hasChanges } from 'utils/common'
import { ApplicationContext } from '../DetailPage/components/ApplicationFormModalV3/applicationForm.context'
import {
  BottomBackButton,
  BottomButton
} from '../DetailPage/components/ApplicationFormModalV3/applicationFormModalV3.styles'
import { ECompleteScreeningSteps } from '../DetailPage/components/ApplicationFormModalV3/components/CompleteScreening/utils'
import FormSubmitted from '../DetailPage/components/ApplicationFormModalV3/components/FormSubmitted'
import StepsSideBar from '../DetailPage/components/ApplicationFormModalV3/components/StepsSideBar'

import {
  EApplicationStepBar,
  DESKTOP_CONTAINER_WIDTH,
  APPLICATION_FEE,
  APPLICATION_TEST_FEE,
  EBottomButtonValue,
  EApplicationCompletedStateV3
} from '../DetailPage/components/ApplicationFormModalV3/constant'
import {
  logStepToAmplitude,
  IApplicationStep,
  fetchClientSecret,
  getReferrers,
  getNewCompletedState,
  checkValidDayOfMonth,
  validatePreviousMoveDate,
  prefillClientInfo,
  validateFormStep
} from '../DetailPage/components/ApplicationFormModalV3/utils'
import { INewApplicationFormValues } from './types'
import { getNewApplicationSteps } from './utils/application-form-v3-handleSteps'
const isProduction: boolean = process.env.NODE_ENV === EnvironmentEnum.PRODUCTION

const NewApplicationFormV3 = () => {
  const method: UseFormReturn<INewApplicationFormValues> = useForm<INewApplicationFormValues>({
    mode: 'onChange',
    defaultValues: {
      confirm: false,
      phoneNumber: '',
      moveInDate: new Date()
    }
  })
  const { setDirty } = useUnsavedWarning()
  const { handleSubmit, control, trigger, setError, clearErrors, reset, getValues, setValue } = method
  const [previousFormValue, setPreviousFormValue] = useState<INewApplicationFormValues>({})
  const formValue: INewApplicationFormValues = useWatch({ control })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isBackLoading, setIsBackLoading] = useState<boolean>(false)
  const [showApplicationForm, setShowApplicationForm] = useState<boolean>(false)
  const { nextStep, activeStep, setStep, prevStep: previousStep, reset: resetStep } = useSteps({ initialStep: 0 })
  const router: NextRouter = useRouter()
  const { query } = router
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState<boolean>(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isDriverLicenseVerified, setIsDriverLicenseVerified] = useState<boolean>(false)
  const [isFetchContinueApplyingData, setIsFetchContinueApplyingData] = useState<boolean>(false)
  const [completeScreeningStep, setCompleteScreeningStep] = useState<number>(
    ECompleteScreeningSteps.PAYMENT_INFORMATION
  )
  const [clientInfo, setClientInfo] = useState<IStripeClient | undefined>(undefined)
  const [checkedItems, setCheckedItems] = useState<string[]>([])
  const [currentHouseApplicationId, setCurrentHouseApplicationId] = useState<string>()
  const { houseApplicationStore, cmsPolicyStore } = useStores()
  const { houseApplication, isTestPhoneNumber, isPaymentExpired } = houseApplicationStore
  const isUsingStepButton: boolean = [
    EApplicationStepBar.PAYMENT,
    EApplicationStepBar.COMPLETE_SCREENING,
    EApplicationStepBar.COMPLETED
  ].includes(activeStep)
  const isMobileTablet: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.LG)
  const steps: IApplicationStep[] = useMemo(() => {
    return getNewApplicationSteps()
  }, [handleNextStep, clientSecret, handlePreviousStep])
  const isDone: boolean = activeStep >= steps.length - 1
  const hasBackButton: boolean = activeStep > EApplicationStepBar.APPLICATION_INFORMATION
  const stepTitle: string = isDone
    ? `${steps.length}. ${steps[steps.length - 1]?.label ?? ''}`
    : `${activeStep + 1}. ${steps[activeStep]?.label ?? ''}`
  const phoneNumber: string = getRawUSPhoneNumber(useWatch({ name: 'phoneNumber', control, defaultValue: '' })!)
  const {
    isApplying: isContinueApplying,
    houseApplicationId: houseApplicationId,
    phoneNumber: queryPhoneNumber
  } = query

  async function onSubmit(): Promise<void> {
    if (previousFormValue?.isDone || !hasChanges(previousFormValue, formValue) || !phoneNumber) {
      return
    }
    const referer: string =
      localStorage.getItem(LocalStorageKeyEnum.REFERRER) || get(document, 'referrer', 'Not updated')
    const sourceUrl: string = referer || location.hostname || 'Not updated'
    const currentUrl: string = get(document, 'URL', '') || get(location, 'href', 'Not updated')
    const lastName: string = formValue?.lastName || extractFromFullName(formValue?.firstName)?.lastName
    const firstName: string = formValue?.firstName || ''
    const lastCompletedState: string =
      sessionStorage.getItem(ESessionStorageKey.APPLICATION_FORM_COMPLETED_STATE) ??
      EApplicationCompletedStateV3.PHONE_NUMBER_SUBMITTED

    try {
      const dateOfBirth: string = `${formValue?.month}/${formValue?.day}/${formValue?.year}`
      if (dayjs(dateOfBirth).isValid()) {
        formValue.dateOfBirth = dateOfBirth
      }
      !dayjs(formValue?.dateOfBirth).isValid() && delete formValue.dateOfBirth
      formValue?.ssn?.includes('X') && delete formValue.ssn

      const monthlyIncome: string | undefined = formValue?.monthlyIncome?.toString().replace('$', '')
      const previousMoveDate: [Date, Date] = get(formValue, 'previousMoveDate', [])
      const [previousMoveInDate, previousMoveOutDate] = validatePreviousMoveDate(previousMoveDate)
      const filteredFormValue: INewApplicationFormValues = omit(formValue, [
        'id',
        'month',
        'day',
        'year',
        'otherReferrer',
        'previousMoveDate',
        'paymentConfirm',
        'promotionCode'
      ])
      const parseValues: INewApplicationFormValues = pickBy(
        {
          ...filteredFormValue,
          sourceUrl,
          currentUrl,
          phoneNumber,
          firstName,
          lastName,
          isDone: activeStep >= steps.length - 1,
          referrers: getReferrers(formValue, filteredFormValue),
          monthlyIncome: Number(monthlyIncome),
          previousLandlordPhoneNumber: getRawUSPhoneNumber(filteredFormValue?.previousLandlordPhoneNumber ?? ''),
          previousMoveInDate,
          previousMoveOutDate,
          lastCompletedState,
          lengthOfStay: Number(formValue?.lengthOfStay)
        },
        identity
      )
      setPreviousFormValue({ ...filteredFormValue })
      // identifyAtlasUser(parseValues)
      const updatedHouseApplication: INewApplicationFormValues = await upsertHousesApplicationByApplicationId(
        omit({ ...parseValues, creditScore: formValue?.creditScore ?? 0 }, ['confirm', 'defaultBuildiumUnitId']),
        currentHouseApplicationId
      )

      setCurrentHouseApplicationId(updatedHouseApplication?.id)
    } catch (error) {
      if (activeStep > EApplicationStepBar.COMPLETE_SCREENING) {
        toast.error('Please make sure you fulfill all fields before moving on')
        resetStep()
      }
      handleError(error as CommonError, 'components/pages/ApplyApplicationPage/index.tsx', 'onSubmit')
    }
  }

  async function handleNextStep(): Promise<void> {
    if (validateFormStep(activeStep, formValue, isMobileTablet)) {
      nextStep()
      activeStep === EApplicationStepBar.PAYMENT && clearErrors('day')
    } else {
      trigger([
        'confirm',
        'firstName',
        'lastName',
        'email',
        'dateOfBirth',
        'ssn',
        'month',
        'year',
        'zipCode',
        'streetName',
        'city',
        'addressLine1',
        'state',
        'monthlyIncome',
        'phoneNumber'
      ])
      validateDateOfBirth()
    }
    document?.getElementById('confirm')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  async function fetchPolicy(): Promise<void> {
    try {
      await cmsPolicyStore.fetchCMSPolicyValue()
    } catch (error) {
      handleError(error as Error, 'components/pages/ApplyApplicationPage/index.tsx', 'fetchPolicy')
    }
  }

  async function handlePreviousStep(): Promise<void> {
    setIsBackLoading(true)
    try {
      if (activeStep === EApplicationStepBar.COMPLETED) {
        setCompleteScreeningStep(ECompleteScreeningSteps.PAYSTUB_UPLOADER)
      }
      previousStep()
    } catch (error) {
      handleError(error as Error, 'components/pages/ApplyApplicationPage/index.tsx', 'handlePreviousStep')
    } finally {
      setIsLoading(false)
      setIsBackLoading(false)
    }
  }

  function updateCompletedStepAndCompletedState(): void {
    const lastCompletedStep: number = parseInt(
      sessionStorage.getItem(ESessionStorageKey.APPLICATION_FORM_COMPLETED_STEP) ?? '0'
    )
    if (activeStep >= lastCompletedStep) {
      sessionStorage.setItem(ESessionStorageKey.APPLICATION_FORM_COMPLETED_STEP, `${activeStep}`)
      const lastCompletedState: string =
        sessionStorage.getItem(ESessionStorageKey.APPLICATION_FORM_COMPLETED_STATE) ?? ''
      const newCompletedState: string = getNewCompletedState(activeStep, lastCompletedState)
      sessionStorage.setItem(ESessionStorageKey.APPLICATION_FORM_COMPLETED_STATE, newCompletedState)
    }
  }

  function handleCompleteScreeningStep(): void {
    if (activeStep === EApplicationStepBar.COMPLETE_SCREENING) {
      //* INFO: if payment isn't expired, skip payment step
      if (completeScreeningStep === ECompleteScreeningSteps.PAYMENT_INFORMATION && !isPaymentExpired) {
        setCompleteScreeningStep(ECompleteScreeningSteps.DRIVER_LICENSE)
      }
    }
  }

  function validateDateOfBirth(): void {
    const isShareYourDeetsStep: boolean = activeStep === EApplicationStepBar.PAYMENT

    //*INFO: default value in order to avoid Invalid Date return from new Date()
    const isValidDayOfMonth: boolean = checkValidDayOfMonth(
      Number(formValue?.month ?? dayjs().month() + 1),
      Number(formValue?.day),
      Number(formValue?.year ?? dayjs().year())
    )
    if (isShareYourDeetsStep && !isValidDayOfMonth) {
      const errorMessage: string = formValue?.day ? 'Invalid day' : 'Day is required'
      setError('day', { message: errorMessage })
    } else {
      clearErrors('day')
    }
  }

  async function fetchClientInfo(): Promise<void> {
    try {
      if (isContinueApplying && houseApplicationId) {
        await houseApplicationStore.fetchHouseApplicationById(String(houseApplicationId), String(queryPhoneNumber), 0)
      }
    } catch (error) {
      handleError(
        error as CommonError,
        'components/pages/DetailPage/components/ApplicationFormModalV3',
        'prefillClientInfo'
      )
    }
  }

  function onClose(): void {
    setIsLoading(true)
    router.push(routes.home.value)
  }

  async function fetchContinueApplyingData(continueApplyingStep: number): Promise<void> {
    const completedStateListV2: EApplicationCompletedStateV3[] = Object.values(EApplicationCompletedStateV3)
    if (completedStateListV2[continueApplyingStep] === EApplicationCompletedStateV3.FULL_APPLICATION_COMPLETED) {
      return
    }
    setIsFetchContinueApplyingData(true)
    setStep(continueApplyingStep)
    if (continueApplyingStep > EApplicationStepBar.APPLICATION_INFORMATION) {
      setValue('confirm', true)
    }
    setIsFetchContinueApplyingData(false)
  }

  useEffect(() => {
    // *INFO: fetch init data
    fetchPolicy()
    sessionStorage.setItem(
      ESessionStorageKey.APPLICATION_FORM_COMPLETED_STATE,
      houseApplication?.lastCompletedState ?? ''
    )
    sessionStorage.setItem(
      ESessionStorageKey.APPLICATION_FORM_COMPLETED_STEP,
      houseApplication?.lastCompletedState ?? '0'
    )
  }, [])

  useEffect(() => {
    if (isContinueApplying && houseApplicationId) {
      fetchClientInfo()
    }
  }, [houseApplicationId, isContinueApplying])

  useEffect(() => {
    const interval = setInterval(() => {
      if (phoneNumber && !isDone && formValue?.email) {
        onSubmit()
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [phoneNumber, formValue, currentHouseApplicationId])

  useEffect(() => {
    updateCompletedStepAndCompletedState()
    !isDone && setDirty(true) //* INFO: Enable unsaved changes warning

    if (phoneNumber && formValue?.email) {
      onSubmit()
    }

    if (
      formValue?.email &&
      formValue?.firstName &&
      formValue?.lastName &&
      (isPaymentExpired || isTestPhoneNumber) &&
      !isPaymentSuccessful
    ) {
      fetchClientSecret(
        setClientSecret,
        setClientInfo,
        isProduction ? APPLICATION_FEE : APPLICATION_TEST_FEE,
        formValue,
        clientInfo
      )
    }

    handleCompleteScreeningStep()
    setTimeout(() => {
      logStepToAmplitude(activeStep)
    }, 0)
  }, [activeStep, isDone, completeScreeningStep])

  useEffect(() => {
    if (isDriverLicenseVerified && activeStep !== EApplicationStepBar.APPLICATION_INFORMATION) {
      onSubmit()
    }
  }, [isDriverLicenseVerified])

  useEffect(() => {
    if (houseApplication?.id) {
      prefillClientInfo(houseApplication, reset, getValues)
      if (houseApplication?.driverLicenseFrontPhotoUrl && houseApplication?.driverLicenseBackPhotoUrl) {
        setIsDriverLicenseVerified(true)
      }
    }
  }, [houseApplication])

  useEffect(() => {
    // *INFO: using delay time to wait for load steps theme
    delay(function () {
      if (!showApplicationForm) {
        setShowApplicationForm(true)
      }
    }, 1000)
  }, [])

  useEffect(() => {
    if (isDriverLicenseVerified) {
      onSubmit()
    }
  }, [isDriverLicenseVerified])

  return (
    <ApplicationContext.Provider
      value={{
        isLoading,
        setIsLoading,
        isBackLoading,
        setIsBackLoading,
        nextStep,
        setStep,
        handleNextStep,
        handlePreviousStep,
        paymentClientSecret: clientSecret,
        isPaymentSuccessful,
        setIsPaymentSuccessful,
        isDriverLicenseVerified,
        setIsDriverLicenseVerified,
        completeScreeningStep,
        setCompleteScreeningStep,
        checkedItems,
        setCheckedItems
      }}
    >
      <HStack
        position="relative"
        height={{ base: 'full', lg: 'auto' }}
        minHeight="100vh"
        flexDirection={{ base: 'column', lg: 'row' }}
        borderRadius={0}
        opacity={showApplicationForm ? 1 : 0}
      >
        <FormProvider {...method}>
          <StepsSideBar
            steps={steps}
            isShowOTPStep={false}
            activeStep={activeStep}
            setStep={setStep}
            completeScreeningStep={completeScreeningStep}
          />
          <Box
            width="full"
            paddingY={2}
            marginTop={{ base: '96px !important', lg: '8px !important' }}
            height={12}
            paddingX={4}
            display={{ lg: 'none' }}
          >
            <Text fontWeight={700} lineHeight={7} fontSize="lg" color="gray.700">
              {stepTitle}
            </Text>
          </Box>
          <chakra.form
            onSubmit={handleSubmit(onSubmit, onSubmit)}
            width="full"
            borderRadius="0"
            height={isDone ? 'unset' : 'full'}
          >
            <Steps hidden={true} activeStep={activeStep} labelOrientation="vertical" responsive={false}>
              {getValidArray(steps).map(({ label, content }) => (
                <Step label={label} key={label}>
                  <VStack
                    spacing={0}
                    textAlign="justify"
                    background="white"
                    height={{ base: 'full', lg: 'auto' }}
                    justifyContent={{ base: 'space-between', lg: 'flex-start' }}
                    marginLeft={{ base: 0, lg: 480 }}
                  >
                    <VStack
                      spacing={0}
                      textAlign="justify"
                      background="white"
                      overflowY="auto"
                      flexGrow={0.9}
                      width="full"
                      height={{ base: 'full', lg: 'auto' }}
                    >
                      {content}
                    </VStack>
                    <HStack
                      width="full"
                      maxWidth={{ base: 'full', lg: DESKTOP_CONTAINER_WIDTH }}
                      alignItems={hasBackButton ? 'space-between' : 'center'}
                      justifyContent={hasBackButton ? 'space-between' : 'center'}
                      paddingBottom={{ base: 6, md: 12, lg: 'auto' }}
                      spacing={2}
                      paddingX={{ base: 4, lg: activeStep === EApplicationStepBar.PAYMENT ? 0 : 4 }}
                      hidden={isDone || isUsingStepButton}
                      position={{ base: 'fixed', lg: 'static' }}
                      bottom={0}
                      zIndex={EZIndexLayer.MODAL}
                      left={0}
                      paddingTop={{ base: 4, lg: 'auto' }}
                      background={{ base: 'white', lg: 'none' }}
                    >
                      {hasBackButton && (
                        <BottomBackButton isLoading={isBackLoading} onClick={handlePreviousStep} variant="solid">
                          {EBottomButtonValue.BACK}
                        </BottomBackButton>
                      )}
                      <BottomButton
                        colorScheme="teal"
                        variant="solid"
                        iconSpacing={2}
                        onClick={handleNextStep}
                        isLoading={isLoading}
                        rightIcon={<ArrowForwardIcon />}
                        isDisabled={formValue?.confirm === false}
                      >
                        {EBottomButtonValue.CONTINUE}
                      </BottomButton>
                    </HStack>
                  </VStack>
                </Step>
              ))}
            </Steps>
            <FormSubmitted isDone={isDone} onClose={onClose} />
          </chakra.form>
        </FormProvider>
      </HStack>
    </ApplicationContext.Provider>
  )
}

export default observer(NewApplicationFormV3)
